import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Seo from '../components/Seo';
import ShareButtons from '../components/ShareButtons';
import { useJobPostingSchema } from './jobDetail/useJobPostingSchema';
import { JobDetailMain } from './jobDetail/JobDetailMain';
import { JobDetailSidebar } from './jobDetail/JobDetailSidebar';
import { JobDetailApplyDialog } from './jobDetail/JobDetailApplyDialog';
import { JobDetailNonMemberDialog } from './jobDetail/JobDetailNonMemberDialog';
import { JobDetailExpressApplyDialog } from './jobDetail/JobDetailExpressApplyDialog';
import { JobDetailExternalApplyDialog } from './jobDetail/JobDetailExternalApplyDialog';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isJobSeeker, loginWithToken } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [nonMemberDialogOpen, setNonMemberDialogOpen] = useState(false);
  const [expressApplyOpen, setExpressApplyOpen] = useState(false);
  const [externalDialogOpen, setExternalDialogOpen] = useState(false);
  const [externalStep, setExternalStep] = useState(1);
  const [starting, setStarting] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);

  // SW Express Apply form state
  const [expressStep, setExpressStep] = useState(1); // 1 = register, 2 = apply, 3 = success
  const [expressForm, setExpressForm] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    license_type: '',
    location: '',
  });
  const [registering, setRegistering] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchJob();
    if (isAuthenticated) {
      checkSaved();
      checkApplied();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isAuthenticated]);

  // JobPosting schema markup + SEO title/description
  useJobPostingSchema(job);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const checkSaved = async () => {
    try {
      const response = await axios.get(`${API_URL}/saved-jobs/check/${jobId}`, {
        headers: {  },
      });
      setIsSaved(response.data.saved);
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const checkApplied = async () => {
    try {
      const response = await axios.get(`${API_URL}/applications/check/${jobId}`, {
        headers: {  },
      });
      setHasApplied(response.data.applied);
    } catch (error) {
      console.error('Error checking application status:', error);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API_URL}/saved-jobs/${jobId}`, {
          headers: {  },
        });
        setIsSaved(false);
        toast.success('Job removed from saved');
      } else {
        await axios.post(`${API_URL}/saved-jobs/${jobId}`, {}, {
          headers: {  },
        });
        setIsSaved(true);
        toast.success('Job saved!');
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleApply = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!isJobSeeker) {
      toast.error('Only job seekers can apply to jobs');
      return;
    }

    setApplying(true);
    try {
      await axios.post(
        `${API_URL}/applications`,
        { job_id: jobId, cover_letter: coverLetter },
        { headers: {  } }
      );
      setHasApplied(true);
      setApplyDialogOpen(false);
      setCoverLetter('');
      toast.success('Application submitted successfully!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit application');
      }
    } finally {
      setApplying(false);
    }
  };

  const handleExternalStart = async () => {
    setStarting(true);
    try {
      const res = await axios.post(`${API_URL}/jobs/${jobId}/external-apply-start`);
      setHasApplied(true);
      setIsSaved(true);
      if (res.data.external_url) {
        window.open(res.data.external_url, '_blank', 'noopener,noreferrer');
      }
      setExternalStep(3);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setStarting(false);
    }
  };

  const handleExternalRegister = async (e) => {
    e.preventDefault();
    if (!expressForm.full_name || !expressForm.email || !expressForm.password || !expressForm.license_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    setRegistering(true);
    try {
      await axios.post(`${API_URL}/auth/register`, {
        email: expressForm.email,
        password: expressForm.password,
        name: expressForm.full_name,
        phone: expressForm.phone,
        user_type: 'job_seeker',
        license_type: expressForm.license_type,
        location: expressForm.location,
        registration_source: 'external_job_application',
        source_job_id: job?.job_id,
        source_job_title: job?.title,
        source_employer: job?.company_name || job?.employer_name,
        source_job_state: job?.state || job?.location,
      });
      const loginRes = await axios.post(`${API_URL}/auth/login`, {
        email: expressForm.email,
        password: expressForm.password,
      });
      await loginWithToken(loginRes.data.token);
      toast.success('Account created! Continue to the employer application.');
      setExternalStep(2);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleApplyClick = () => {
    // External / aggregated job - require a free account, save interest, then redirect
    if (job.is_external || job.application_type === 'external') {
      if (isAuthenticated && !isJobSeeker) {
        toast.error('Only job seekers can apply to jobs');
        return;
      }
      setExternalDialogOpen(true);
      setExternalStep(isAuthenticated ? 2 : 1);
      return;
    }

    // Not authenticated - show SW Express Apply modal
    if (!isAuthenticated) {
      setExpressApplyOpen(true);
      setExpressStep(1);
      return;
    }

    if (!isJobSeeker) {
      toast.error('Only job seekers can apply to jobs');
      return;
    }

    // Non-member employer - show blocking modal
    if (!job.employer_is_member && !job.can_apply_on_platform) {
      setNonMemberDialogOpen(true);
      return;
    }

    // Member employer - open apply dialog
    setApplyDialogOpen(true);
  };

  // Handle SW Express Apply registration
  const handleExpressRegister = async (e) => {
    e.preventDefault();

    if (!expressForm.full_name || !expressForm.email || !expressForm.password || !expressForm.license_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setRegistering(true);
    try {
      // Register the user
      await axios.post(`${API_URL}/auth/register`, {
        email: expressForm.email,
        password: expressForm.password,
        name: expressForm.full_name,
        phone: expressForm.phone,
        user_type: 'job_seeker',
        license_type: expressForm.license_type,
      });

      // Auto-login
      const loginRes = await axios.post(`${API_URL}/auth/login`, {
        email: expressForm.email,
        password: expressForm.password,
      });

      // Cookie set by /auth/login; refresh auth context
      await loginWithToken(loginRes.data.token);

      toast.success('Account created! Now submit your application.');

      // Move to step 2 (apply)
      setExpressStep(2);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  // Handle SW Express Apply submission (after registration)
  const handleExpressApply = async () => {
    setApplying(true);
    try {
      await axios.post(
        `${API_URL}/applications`,
        { job_id: jobId, cover_letter: coverLetter },
        { headers: {  } }
      );
      setHasApplied(true);
      setCoverLetter('');
      setExpressStep(3); // Move to success step
      toast.success('Application submitted successfully!');
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit application');
      }
    } finally {
      setApplying(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: job.title,
        text: `Check out this job: ${job.title} at ${job.company_name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if ((job && job.is_expired) || notFound) {
    const t = job?.title;
    return (
    <div className="min-h-screen bg-gray-50 pt-20" data-testid={notFound ? 'job-not-found' : 'job-expired'}>
      <Seo
        title="Position No Longer Available"
        description="This social work position is no longer accepting applications. Browse current social work jobs on I Need A Social Worker."
        type="article"
        noindex
      />
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-serif text-3xl font-bold text-gray-900">This position is no longer available</h1>
        <p className="text-gray-600 mt-3">
          {t ? `“${t}”${job?.company ? ` at ${job.company}` : ''} has expired or been filled. ` : 'That job posting has expired, been filled, or the link is no longer valid. '}
          Explore current social work jobs instead.
        </p>
        <div className="mt-7 flex flex-wrap justify-center gap-2" data-testid="unavailable-alternatives">
          {[
            ['All social work jobs', '/jobs'],
            ['LCSW jobs', '/lcsw-jobs'],
            ['LMSW jobs', '/lmsw-jobs'],
            ['Remote jobs', '/remote-social-work-jobs'],
            ['Hospice jobs', '/hospice-social-worker-jobs'],
            ['School jobs', '/school-social-worker-jobs'],
            ['By state', '/social-work-jobs-by-state'],
          ].map(([label, to]) => (
            <Link key={to} to={to} className="px-4 py-2 rounded-full text-sm bg-white border border-gray-200 text-primary hover:border-secondary transition-colors">{label}</Link>
          ))}
        </div>
        <Link to="/jobs" className="mt-6 inline-flex items-center gap-2 text-primary font-semibold" data-testid="expired-back-jobs">
          <ArrowLeft className="w-4 h-4" /> Browse active jobs
        </Link>
      </div>
    </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        title={`${job.title}${(job.company_name && job.company_name !== 'undefined') ? ` at ${job.company_name}` : ((job.employer_name && job.employer_name !== 'undefined') ? ` at ${job.employer_name}` : '')}`}
        description={`${job.title} — ${[(job.company_name && job.company_name !== 'undefined') ? job.company_name : ((job.employer_name && job.employer_name !== 'undefined') ? job.employer_name : null), job.location, job.is_remote ? 'Remote' : null].filter(Boolean).join(' · ')}. Apply on I Need A Social Worker.`}
        type="article"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Link
            to="/jobs"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Jobs
          </Link>
          <ShareButtons title={job.title} text={`${job.title} — a social work job on I Need A Social Worker`} testidPrefix="job-share" />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <JobDetailMain job={job} />
          <JobDetailSidebar
            job={job}
            hasApplied={hasApplied}
            handleApplyClick={handleApplyClick}
            handleSave={handleSave}
            isSaved={isSaved}
            handleShare={handleShare}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </div>

      {/* Apply Dialog */}
      <JobDetailApplyDialog
        open={applyDialogOpen}
        onOpenChange={setApplyDialogOpen}
        job={job}
        user={user}
        coverLetter={coverLetter}
        setCoverLetter={setCoverLetter}
        applying={applying}
        handleApply={handleApply}
      />

      {/* Non-Member Employer Dialog */}
      <JobDetailNonMemberDialog
        open={nonMemberDialogOpen}
        onOpenChange={setNonMemberDialogOpen}
        onBrowseMember={() => {
          setNonMemberDialogOpen(false);
          navigate('/jobs?member=true');
        }}
      />

      {/* SW Express Apply Dialog - For New Users */}
      <JobDetailExpressApplyDialog
        open={expressApplyOpen}
        onOpenChange={setExpressApplyOpen}
        job={job}
        expressStep={expressStep}
        setExpressStep={setExpressStep}
        expressForm={expressForm}
        setExpressForm={setExpressForm}
        registering={registering}
        handleExpressRegister={handleExpressRegister}
        coverLetter={coverLetter}
        setCoverLetter={setCoverLetter}
        applying={applying}
        handleExpressApply={handleExpressApply}
        onSignIn={() => {
          setExpressApplyOpen(false);
          navigate('/login');
        }}
        onTrackApplication={() => {
          setExpressApplyOpen(false);
          navigate('/dashboard');
        }}
      />

      {/* External Apply Dialog - aggregated/external jobs */}
      <JobDetailExternalApplyDialog
        open={externalDialogOpen}
        onOpenChange={setExternalDialogOpen}
        job={job}
        externalStep={externalStep}
        setExternalStep={setExternalStep}
        expressForm={expressForm}
        setExpressForm={setExpressForm}
        registering={registering}
        handleExternalRegister={handleExternalRegister}
        starting={starting}
        handleExternalStart={handleExternalStart}
        onSignIn={() => {
          setExternalDialogOpen(false);
          navigate('/login');
        }}
        onGoDashboard={() => {
          setExternalDialogOpen(false);
          navigate('/dashboard');
        }}
      />
    </div>
  );
};

export default JobDetail;
